import type {
  DealAnalysis,
  PriceHistoryPoint,
  ProductDetail,
} from '../types';

/**
 * Badges para la página de detalle. Se calculan en cliente a partir de los datos
 * que ya se cargan ahí (`/products/:id`, `/history`, `/analysis`) — no agregan
 * carga al backend.
 *
 * Cada badge tiene un `tone` para colorear y un `title` opcional con el detalle
 * en hover.
 */

export type BadgeTone = 'good' | 'bad' | 'neutral' | 'warn';

export type Badge = {
  label: string;
  tone: BadgeTone;
  title?: string;
};

const DAY_MS = 24 * 3600_000;

export function computeBadges(args: {
  product: ProductDetail;
  history: PriceHistoryPoint[];
  analysis: DealAnalysis | null;
}): Badge[] {
  const { product, history, analysis } = args;
  const current = product.current_price_cents;
  if (current == null) return [];

  const badges: Badge[] = [];
  const now = Date.now();

  // Mínimo observado en la ventana cargada — solo si hay variación real.
  if (history.length >= 5) {
    const prices = history.map((h) => h.current_price_cents);
    const minObs = Math.min(...prices);
    const maxObs = Math.max(...prices);
    const median = [...prices].sort((a, b) => a - b)[Math.floor(prices.length / 2)];
    const variance = median > 0 ? (maxObs - minObs) / median : 0;
    if (variance >= 0.05 && current <= minObs * 1.005) {
      badges.push({
        label: 'Mínimo histórico',
        tone: 'good',
        title: `S/${(current / 100).toFixed(2)} = mínimo observado en ${history.length} puntos`,
      });
    }
  }

  // Variación últimos 7 días.
  const change7d = computeChange(history, current, now - 7 * DAY_MS);
  if (change7d != null && Math.abs(change7d) >= 3) {
    badges.push(
      change7d > 0
        ? { label: `Subió ${change7d}% esta semana`, tone: 'bad' }
        : { label: `Bajó ${Math.abs(change7d)}% esta semana`, tone: 'good' },
    );
  }

  // Variación últimos 30 días — solo cuando hay suficiente historia.
  if (history.length >= 14) {
    const change30d = computeChange(history, current, now - 30 * DAY_MS);
    if (change30d != null) {
      if (change30d <= -5) {
        badges.push({
          label: `${Math.abs(change30d)}% bajo el mes pasado`,
          tone: 'good',
        });
      } else if (change30d >= 8) {
        badges.push({
          label: `${change30d}% sobre el mes pasado`,
          tone: 'bad',
        });
      }
    }
  }

  // Precio raro (extraído del analysis del backend).
  if (analysis && analysis.daysBelowCurrent <= 7 && history.length >= 14) {
    badges.push({
      label: `Precio raro · ${analysis.daysBelowCurrent} días/año a este nivel`,
      tone: 'good',
    });
  }

  // Volvió al precio normal: current ≈ mediana 180d, pero hubo un bajón reciente.
  if (analysis && analysis.median180dCents > 0 && history.length >= 20) {
    const ratio = current / analysis.median180dCents;
    if (ratio >= 0.97 && ratio <= 1.03) {
      const recent = history.filter(
        (h) => now - new Date(h.captured_at).getTime() <= 30 * DAY_MS,
      );
      if (recent.length > 0) {
        const recentMin = Math.min(...recent.map((h) => h.current_price_cents));
        if (recentMin < current * 0.95) {
          badges.push({ label: 'Volvió al precio normal', tone: 'neutral' });
        }
      }
    }
  }

  // "Antes" inflado: precio tachado no calza con el histórico.
  if (analysis && analysis.listPriceIsReal === false) {
    badges.push({
      label: '"Antes" inflado',
      tone: 'warn',
      title:
        'El precio tachado de la tienda está bastante por encima del precio normal observado en el histórico',
    });
  }

  // Nuevo en catálogo: visto por primera vez hace menos de una semana.
  if (product.first_seen_at) {
    const age = now - new Date(product.first_seen_at).getTime();
    if (age <= 7 * DAY_MS) {
      badges.push({ label: 'Nuevo en catálogo', tone: 'neutral' });
    }
  }

  return badges;
}

/**
 * Devuelve el % de cambio entre el precio actual y el punto histórico más
 * cercano al `targetTime` (en el pasado), o null si no hay un punto dentro
 * de la tolerancia de 3 días.
 */
function computeChange(
  history: PriceHistoryPoint[],
  current: number,
  targetTime: number,
): number | null {
  let closest: PriceHistoryPoint | null = null;
  let closestDiff = Infinity;

  for (const h of history) {
    const t = new Date(h.captured_at).getTime();
    if (t > targetTime) continue;
    const diff = targetTime - t;
    if (diff < closestDiff) {
      closestDiff = diff;
      closest = h;
    }
  }

  if (!closest || closestDiff > 3 * DAY_MS) return null;
  const old = closest.current_price_cents;
  if (old <= 0) return null;
  return Math.round(((current - old) / old) * 100);
}
